import React, { useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Box, Typography, Grid, TextField, CircularProgress } from "@mui/material";
import AppLoading from "verticales/AppLoading.tsx";
import TableMaestros from "../../../verticales/TableMaestrosInformes.tsx";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import MDBox from "components/MDBox";
// import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Autocomplete from "@mui/material/Autocomplete";
import dayjs from "dayjs";
import fetchData from "verticales/FetchData";

const theme = createTheme({
  components: {
    MuiMenuItem: {
      styleOverrides: {
        root: {
          "&.MuiMultiSectionDigitalClockSection-item": {
            fontSize: "1rem",
          },
        },
      },
    },
  },
});

const optionsCpu = [
  { label: "Consumos de CPU entre 0% - 10%", id: "between:0:and:10" },
  { label: "Consumos de CPU entre 10% - 20%", id: "between:10:and:20" },
  { label: "Consumos de CPU entre 20% - 30%", id: "between:20:and:30" },
  { label: "Consumos de CPU entre 30% - 40%", id: "between:30:and:40" },
  { label: "Consumos de CPU entre 40% - 50%", id: "between:40:and:50" },
  { label: "Consumos de CPU entre 50% - 60%", id: "between:50:and:60" },
  { label: "Consumos de CPU entre 60% - 70%", id: "between:60:and:70" },
  { label: "Consumos de CPU entre 70% - 80%", id: "between:70:and:80" },
  { label: "Consumos de CPU entre 80% - 90%", id: "between:80:and:00" },
  { label: "Consumos de CPU entre 90% - 100%", id: "between:90:and:100" },
];

const url = "https://5djo5qwa3l.execute-api.us-east-1.amazonaws.com/dimo-goyito/ec2/";
const context = "informes";

const getEstadoBadge = (cpuValue, onClick) => {
  let color = "sucess";
  let label = "Bajo";

  if (cpuValue >= 80) {
    color = "error";
    label = "Alto";
  } else if (cpuValue >= 40) {
    color = "warning";
    label = "OK";
  }

  return (
    <MDBadge
      badgeContent={label}
      color={color}
      variant="gradient"
      size="sm"
      onClick={() => onClick(label)}
      sx={{
        cursor: "pointer",
        minWidth: 70,
        justifyContent: "center",
        display: "flex",
        textAlign: "center",
        paddingX: "6px",
      }}
    />
  );
};

const ViewDiscos = ({ data = [], loading = false }) => {
  const [loadingState, setLoadingState] = useState(true);
  const [filteredData, setFilteredData] = useState([]);
  const [fechaIni, setFechaIni] = useState(dayjs(new Date()));
  const [fechaFin, setFechaFin] = useState(dayjs(new Date()));
  const [prcSatura, setPrcSatura] = useState(null);
  const [estadoFilter, setEstadoFilter] = useState(null);
  const [blocking, setBlocking] = useState(false);

  const handleButtonClick = async () => {
    setBlocking(true);
    const dateIni = fechaIni ? fechaIni.toDate().getTime() : null;
    const dateFin = fechaFin ? fechaFin.toDate().getTime() : null;
    const params = {
      action: "ec2-metricas-saturacion",
      object: {
        prcSatura,
        dateIni,
        dateFin,
      },
    };
    try {
      const resp = await fetchData(url, context, params);
      setFilteredData(resp.rows);
    } catch (error) {
      console.error("Error fetching data: ", error);
      setFilteredData([]);
    } finally {
      setBlocking(false);
    }
  };

  const handleExportData = () => {
    const rowsToExport = dataToDisplay;

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [
        Object.keys(rowsToExport[0] || {}).join(","),
        ...rowsToExport.map((row) =>
          Object.values(row)
            .map((val) => `${val}`)
            .join(",")
        ),
      ].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "datos_exportados.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const dataToDisplay = useMemo(() => {
    if (!estadoFilter) return filteredData;

    return filteredData.filter((item) => {
      const estado =
        item.promedio_cpu_maximo >= 75 ? "Alto" : item.promedio_cpu_maximo >= 50 ? "OK" : "Bajo";
      return estado === estadoFilter;
    });
  }, [filteredData, estadoFilter]);

  const columns = useMemo(
    () => [
      { accessorKey: "cuenta", header: "Cuenta", size: 150 },
      { accessorKey: "instance_id", header: "Instance ID", size: 200 },
      {
        accessorKey: "instance_name",
        header: "Instance Name",
        size: 300,
        enableGrouping: false,
      },
      {
        accessorKey: "promedio_cpu_maximo",
        header: "CPU saturación",
        size: 200,
      },
      { accessorKey: "promedio_ram_maximo", header: "Ram", size: 200 },
      { accessorKey: "tiempo_minutos", header: "Tiempo (minutos)", size: 200 },
      {
        accessorKey: "total_metricas_analizadas",
        header: "# metricas ",
        size: 200,
      },
      {
        accessorKey: "estado",
        header: "Estado",
        size: 120,
        Cell: ({ row }) =>
          getEstadoBadge(row.original.promedio_cpu_maximo, (estado) => {
            setEstadoFilter((prev) => (prev === estado ? null : estado));
          }),
      },
      { accessorKey: "producto", header: "Producto ", size: 200 },
      { accessorKey: "cliente", header: "Cliente ", size: 200 },
      { accessorKey: "instance_type", header: "Tipo", size: 200 },
    ],
    []
  );

  useEffect(() => {
    if (data.length > 0) {
      setLoadingState(false);
    } else {
      setLoadingState(true);
    }
  }, [data]);

  return (
    <Box sx={{ marginTop: "1px" }}>
      <MDBox m={1}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box components={[]}>
            <Autocomplete
              disablePortal
              options={optionsCpu}
              sx={{ width: 300 }}
              onInputChange={(event, newCuentaValue) => {
                setPrcSatura(newCuentaValue);
              }}
              value={prcSatura}
              renderInput={(params) => <TextField {...params} label="Rango Prc Saturacion CPU" />}
            />
            <ThemeProvider theme={theme}>
              <DateTimePicker
                label="inicial timestamp"
                onChange={(newValue) => setFechaIni(newValue)}
                value={fechaIni}
              />
              <DateTimePicker
                label="final timestamp"
                onChange={(newValue) => setFechaFin(newValue)}
                value={fechaFin}
              />
            </ThemeProvider>
            <MDButton
              variant="contained"
              disableElevation
              onClick={handleButtonClick}
              disabled={blocking}
            >
              Buscar
            </MDButton>
          </Box>
        </LocalizationProvider>
      </MDBox>

      {blocking ? (
        <AppLoading titleCustom="Métricas EC2" />
      ) : (
        <TableMaestros
          height={250}
          arrayColumns={columns}
          data={dataToDisplay}
          enableGrouping
          enableStickyHeader
          enableColumnResizing
          muiTableContainerProps={{
            sx: { maxHeight: "calc(100vh - 445px)", minHeight: "150px" },
          }}
          muiTableBodyCellProps={{
            sx: {
              borderRight: "1px solid #e0e0e0",
              fontSize: 14,
              padding: "0.5rem",
            },
          }}
          muiTableHeadCellProps={{
            sx: (theme) => ({
              backgroundColor: theme.palette.mode === "light" ? "#f5f5f5" : "#303030",
              fontSize: "14px",
              fontWeight: "bold",
            }),
          }}
          renderTopToolbarCustomActions={() => (
            <Box sx={{ display: "flex", gap: "16px", flexWrap: "wrap", padding: "4px" }}>
              <MDButton
                onClick={handleExportData}
                startIcon={<FileDownloadIcon />}
                sx={{ fontSize: "12px", fontWeight: "normal" }}
              >
                Exportar Datos
              </MDButton>
              {estadoFilter && (
                <MDButton
                  onClick={() => setEstadoFilter(null)}
                  color="info"
                  variant="outlined"
                  sx={{ fontSize: "12px", fontWeight: "normal" }}
                >
                  Limpiar filtro de estado
                </MDButton>
              )}
            </Box>
          )}
          initialState={{
            density: "compact",
            pagination: { pageIndex: 0, pageSize: 50 },
          }}
        />
      )}
    </Box>
  );
};

ViewDiscos.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      cuenta: PropTypes.string,
      instance_id: PropTypes.string.isRequired,
      instance_name: PropTypes.string,
      ip_privada: PropTypes.string,
      ip_publica: PropTypes.string,
      disk: PropTypes.string,
      total_size: PropTypes.number,
      used_space: PropTypes.number,
      free_space: PropTypes.number,
      free_percent: PropTypes.number,
    })
  ),
  loading: PropTypes.bool,
};

export default ViewDiscos;
